<template>
  <header class="boq-detail__head">
    <div class="boq-detail__headinfo">
      <div class="boq-detail__titlerow">
        <b-icon pack="fas" icon="folder-open" size="is-small" class="boq-detail__picker-ico" />
        <span class="boq-detail__title">{{ boq.name }}</span>
        <b-tag :type="tagType(boq.type)" size="is-small" rounded class="epc-tag--compact">
          {{ $t(typeShortKey(boq.type)) }}
        </b-tag>
        <!-- Único punto clicable para cambiar de carga (oculto mientras carga el detalle). -->
        <button v-show="!loading" type="button" class="boq-detail__change" @click="$emit('change-boq')">
          <b-icon pack="fas" icon="chevron-down" size="is-small" /> {{ $t('change_boq') }}
        </button>
      </div>
      <!-- Orden de pedido vinculada + subcontrata (solo civil; — si no tiene) -->
      <div v-if="isCivil" class="boq-detail__order">
        <b-icon pack="fas" icon="file-invoice" size="is-small" class="mr-1" />
        <span class="boq-detail__order-label">{{ $t('linked_work_order') }}:</span>
        <span class="boq-detail__order-val" :title="boq.work_order && boq.work_order.description">
          {{ boq.work_order ? orderText(boq.work_order) : '—' }}
        </span>
        <span class="boq-detail__order-label ml-3">{{ $t('subcontractor') }}:</span>
        <span class="boq-detail__order-muted">{{ (boq.work_order && boq.work_order.partner) || '—' }}</span>
      </div>
    </div>

    <div v-show="!loading" class="boq-detail__actions">
      <!-- Estado / botón de validación de la carga BOQ. -->
      <b-tooltip
        v-if="!validated && blocking > 0"
        :label="validateHint"
        position="is-bottom"
        type="is-warning"
        multilined
      >
        <span class="boq-validate boq-validate--obs">
          <b-icon pack="fas" icon="triangle-exclamation" size="is-small" />
          {{ validateChipLabel }} ({{ blocking }})
        </span>
      </b-tooltip>

      <button
        v-else-if="!validated"
        type="button"
        class="boq-validate boq-validate--btn"
        :disabled="validating"
        @click="$emit('validate', true)"
      >
        <b-icon pack="fas" icon="circle-check" size="is-small" />
        {{ $t('validate') }}
      </button>

      <b-tooltip
        v-else
        :label="validatedTooltip"
        position="is-bottom"
        type="is-dark"
        multilined
      >
        <button
          type="button"
          class="boq-validate boq-validate--ok"
          :disabled="validating"
          @click="$emit('validate', false)"
        >
          <b-icon pack="fas" icon="circle-check" size="is-small" />
          {{ $t('validated') }}
        </button>
      </b-tooltip>

      <!-- Acciones agrupadas en un menú (kebab). -->
      <b-dropdown aria-role="list" position="is-bottom-left">
        <template #trigger>
          <button type="button" class="epc-icon-button" :title="$t('actions')">
            <b-icon pack="fas" icon="ellipsis-vertical" size="is-small" />
          </button>
        </template>
        <b-dropdown-item aria-role="listitem" @click="$emit('update')">
          <b-icon pack="fas" icon="rotate" size="is-small" class="mr-1" />{{ $t('update') }}
        </b-dropdown-item>
        <!-- Vinculación BOQ ↔ WBS (civil, mecánico y eléctrico). -->
        <b-dropdown-item aria-role="listitem" @click="$emit('link')">
          <b-icon pack="fas" icon="link" size="is-small" class="mr-1" />{{ $t('boq_link') }}
        </b-dropdown-item>
        <!-- Reasociar orden de pedido: solo para BOQ civil. -->
        <b-dropdown-item v-if="isCivil" aria-role="listitem" @click="$emit('work-order')">
          <b-icon pack="fas" icon="file-invoice" size="is-small" class="mr-1" />{{ $t('work_order') }}
        </b-dropdown-item>
        <!-- Marcar en masa como "sin mediciones" (solo líneas sin asignaciones). -->
        <b-dropdown-item aria-role="listitem" @click="$emit('mark-no-measurement-link')">
          <b-icon pack="fas" icon="link-slash" size="is-small" class="mr-1" />{{ $t('mark_no_measurement_link_bulk') }}
        </b-dropdown-item>
        <b-dropdown-item v-if="boq.document && boq.document.download_url"
                        aria-role="listitem" has-link>
          <a :href="boq.document.download_url" target="_blank" rel="noopener">
            <b-icon pack="fas" icon="download" size="is-small" class="mr-1" />{{ $t('download') }}
          </a>
        </b-dropdown-item>
        <!-- Acción de importación (grupo aparte). -->
        <hr class="dropdown-divider" />
        <b-dropdown-item aria-role="listitem" @click="$emit('import')">
          <b-icon pack="fas" icon="file-import" size="is-small" class="mr-1" />{{ $t('import_new_boq') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </header>
</template>

<script>
  export default {
    name: 'BOQDetailsHeader',

    props: {
      // Carga seleccionada (cabecera).
      boq: { type: Object, required: true },
      // El detalle está cargando (oculta cambiar-carga y acciones).
      loading: { type: Boolean, default: false },
      // Estado de validación de la carga.
      validated: { type: Boolean, default: false },
      validating: { type: Boolean, default: false },
      // Nº de líneas que impiden validar (observadas + sin asignar) + textos derivados.
      blocking: { type: Number, default: 0 },
      validateHint: { type: String, default: '' },
      validateChipLabel: { type: String, default: '' },
      validatedTooltip: { type: String, default: '' },
      // ¿Es carga civil? (habilita orden de pedido / reasociar).
      isCivil: { type: Boolean, default: false },
    },

    methods: {
      // El API serializa el enum BoqType como su valor. Numeración unificada:
      // 1=civil, 2=mecánico, 3=eléctrico.
      typeShortKey(type) {
        const n = Number(type);
        if (n === 1) return 'civil';
        if (n === 2) return 'mechanical';
        return 'electrical';
      },
      tagType(type) {
        const n = Number(type);
        if (n === 1) return 'is-success';   // civil
        if (n === 2) return 'is-info';      // mecánico
        return 'is-warning';                 // eléctrico
      },
      // Texto "code · descripción" de una orden ya asociada.
      orderText(wo) {
        if (!wo) return '—';
        return wo.description ? `${wo.code} · ${wo.description}` : `${wo.code}`;
      },
    },
  };
</script>

<style scoped>
.boq-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1f7;
}
/* Ocupa el ancho restante; si actions crece, info se reduce (pero nunca desaparece). */
.boq-detail__headinfo { flex: 1 1 auto; min-width: 0; }
.boq-detail__title {
  font-size: 16px;
  font-weight: 700;
  color: #33518f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
/* Fila de título del detalle: carpeta + nombre + badge + acción "Gestionar otra BOQ". */
.boq-detail__titlerow {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.boq-detail__picker-ico { color: #7d8aa5; flex: 0 0 auto; }

/* Único elemento clicable para cambiar de carga (chip discreto). */
.boq-detail__change {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  padding: 2px 8px;
  border: 1px solid #d9e0ee;
  border-radius: 9999px;
  background: #fff;
  color: #3d5c9e;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;
}
.boq-detail__change:hover { background: #f2f6ff; border-color: #7d99d4; }

/* Orden de pedido vinculada (solo civil). */
.boq-detail__order {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}
.boq-detail__order-label { color: #7d8aa5; font-weight: 600; flex: 0 0 auto; }
.boq-detail__order-val {
  color: #33518f;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.boq-detail__order-muted { color: #9aa7c0; flex: 0 0 auto; }

/* Zona de acciones del detalle: control de validación + menú kebab. */
.boq-detail__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
/* Botón / etiqueta de validación de la carga. */
.boq-validate {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  border: 1px solid transparent;
  white-space: nowrap;
}
.boq-validate--btn { color: #fff; background: rgb(58, 107, 200); cursor: pointer; }
.boq-validate--btn:hover { filter: brightness(0.93); }
.boq-validate--btn:disabled { opacity: .6; cursor: not-allowed; }
.boq-validate--ok {
  color: #1f7a44;
  background: #e3fcec;
  border-color: rgba(31,122,68,.35);
  cursor: pointer;
}
.boq-validate--obs {
  color: #9a6a00;
  background: #fef3c7;
  border-color: rgba(154,106,0,.3);
}
</style>
